import { el } from '../dom.js';
import type { I18n, Lang } from '../i18n.js';
import { SUPPORTED_LANGS } from '../i18n.js';
import { BrandLogo } from './brandLogo.js';
import { isExpertMode, setExpertMode } from '../expertMode.js';

export interface LandingProps {
  i18n: I18n;
  onStart: (lang: Lang) => void;
  onOpenPrivacy: () => void;
  /**
   * SEO.3 — researcher-tier link. Shown as a discrete footer link on landing;
   * never surfaces the ethical axes / role-bias details (G1 anti-priming).
   */
  onOpenAboutResearch?: () => void;
}

export function LandingView({ i18n, onStart, onOpenPrivacy, onOpenAboutResearch }: LandingProps): HTMLElement {
  const consent = el('input', { type: 'checkbox', id: 'consent' });
  const ageOk = el('input', { type: 'checkbox', id: 'age' });
  const expertMode = el('input', {
    type: 'checkbox',
    id: 'expert-mode',
  }) as HTMLInputElement;
  expertMode.checked = isExpertMode();
  expertMode.addEventListener('change', () => setExpertMode(expertMode.checked));

  const begin = el(
    'button',
    { class: 'block', disabled: true },
    i18n.t('landing.begin'),
  ) as HTMLButtonElement;

  const updateBegin = () => {
    begin.disabled = !(consent.checked && ageOk.checked);
  };
  consent.addEventListener('change', updateBegin);
  ageOk.addEventListener('change', updateBegin);

  begin.addEventListener('click', () => onStart(i18n.lang));

  const langSelect = el(
    'select',
    { class: 'lang-select', 'aria-label': i18n.t('landing.language') },
    SUPPORTED_LANGS.map((l) =>
      el('option', { value: l, selected: l === i18n.lang || undefined }, l.toUpperCase()),
    ),
  );
  langSelect.addEventListener('change', async () => {
    const next = (langSelect as HTMLSelectElement).value as Lang;
    await i18n.switch(next);
    window.dispatchEvent(new CustomEvent('allot:langChanged'));
  });

  const brandLink = el(
    'a',
    {
      class: 'brand-mark',
      href: 'https://www.linkedin.com/company/confluxlab',
      target: '_blank',
      rel: 'noopener noreferrer',
      'aria-label': i18n.t('app.title') + ' — ' + i18n.t('app.by_conflux'),
    },
    [
      BrandLogo(28),
      el('span', { class: 'brand-text' }, [
        el('span', { class: 'brand-title' }, i18n.t('app.title')),
        el('span', { class: 'brand-sub' }, i18n.t('app.by_conflux')),
      ]),
    ],
  );

  const topbar = el('div', { class: 'topbar' }, [
    el('div', { class: 'topbar-inner' }, [
      brandLink,
      el('span', { class: 'topbar-spacer' }),
      langSelect,
    ]),
  ]);

  const hero = el('section', { class: 'hero' }, [
    el('p', { class: 'hero-eyebrow' }, i18n.t('app.tagline')),
    el('h1', { class: 'hero-title' }, i18n.t('landing.heading')),
    el('p', { class: 'hero-lede' }, i18n.t('landing.summary')),
    el('div', { class: 'meta-row' }, [
      el('span', { class: 'meta-pill' }, i18n.t('landing.duration')),
      el('span', { class: 'meta-pill' }, i18n.t('landing.scenarios')),
      el('span', { class: 'meta-pill' }, i18n.t('landing.anonymity')),
    ]),
    // §7.1 v2.3 — session grew from 13 to 27 slots; make the pause/resume
    // affordance explicit up front so long length is not a surprise.
    el('p', { class: 'hero-resume-hint' }, i18n.t('landing.resume_hint')),
  ]);

  const mission = el('details', { class: 'panel panel-collapsible mission-panel' }, [
    el('summary', {}, i18n.t('landing.mission.toggle')),
    el('div', { class: 'mission-body' }, [
      el('h2', { class: 'mission-heading' }, i18n.t('landing.mission.legacy.heading')),
      el('p', { class: 'mission-intro' }, i18n.t('landing.mission.legacy.body')),

      el('h2', { class: 'mission-heading' }, i18n.t('landing.mission.heading')),
      el('p', { class: 'mission-intro' }, i18n.t('landing.mission.intro')),
      el('ul', { class: 'mission-points' }, [
        el('li', {}, [
          el('strong', {}, i18n.t('landing.mission.bias.heading') + ': '),
          el('span', {}, i18n.t('landing.mission.bias.body')),
        ]),
        el('li', {}, [
          el('strong', {}, i18n.t('landing.mission.fear.heading') + ': '),
          el('span', {}, i18n.t('landing.mission.fear.body')),
        ]),
        el('li', {}, [
          el('strong', {}, i18n.t('landing.mission.empathy.heading') + ': '),
          el('span', {}, i18n.t('landing.mission.empathy.body')),
        ]),
      ]),

      el('h2', { class: 'mission-heading' }, i18n.t('landing.mission.lab.heading')),
      el('p', { class: 'mission-intro' }, i18n.t('landing.mission.lab.body')),

      el('h2', { class: 'mission-heading' }, i18n.t('landing.mission.commons.heading')),
      el('p', { class: 'mission-intro' }, i18n.t('landing.mission.commons.body')),

      el('h2', { class: 'mission-heading' }, i18n.t('landing.mission.service.heading')),
      el('p', { class: 'mission-intro' }, i18n.t('landing.mission.service.body')),

      el('h2', { class: 'mission-heading' }, i18n.t('landing.mission.manifesto.heading')),
      el('p', { class: 'mission-intro' }, i18n.t('landing.mission.manifesto.body')),
    ]),
  ]);

  // DR-UX-1 — trigger warning visible BEFORE the consent checkboxes and Begin
  // button. IASC MHPSS and informed-consent best practice require sensitive-
  // content disclosure to be visible by default, not hidden behind a
  // collapsible. Keep the collapsible too for participants who want more
  // detail — redundancy is acceptable for an ethics affordance.
  const sensitiveBanner = el(
    'aside',
    { class: 'sensitive-banner', role: 'note', 'aria-labelledby': 'sensitive-banner-title' },
    [
      el('div', { class: 'sensitive-banner-body' }, [
        el('h2', { id: 'sensitive-banner-title', class: 'sensitive-banner-title' },
          i18n.t('landing.sensitive_heading')),
        el('p', { class: 'sensitive-banner-text' }, i18n.t('landing.sensitive_body')),
        el('p', { class: 'sensitive-banner-resources' }, [
          el('span', {}, i18n.t('landing.sensitive_resources').replace(/mhpss\.net/, '').trim() + ' '),
          el('a', { href: 'https://mhpss.net', target: '_blank', rel: 'noopener noreferrer' }, 'mhpss.net'),
        ]),
      ]),
    ],
  );

  const sensitive = el('details', { class: 'panel panel-collapsible' }, [
    el(
      'summary',
      {},
      i18n.has('consent.sensitive_notice_heading')
        ? i18n.t('consent.sensitive_notice_heading')
        : 'Sensitive content — more detail',
    ),
    el('p', {}, i18n.t('consent.sensitive_notice')),
  ]);

  // "How the study works" — 4-step walkthrough. Collapsible so landing
  // stays compact, but open-by-default-on-first-visit is left to a
  // future enhancement (localStorage flag). Content is G3-neutral and
  // maps exactly to the in-session UI (Confirm / Decline / Split /
  // I can't decide).
  const howItWorks = el('details', { class: 'panel panel-collapsible how-panel' }, [
    el('summary', {}, i18n.t('landing.how.toggle')),
    el('ol', { class: 'how-steps' }, [
      el('li', {}, [
        el('h3', { class: 'how-step-title' }, i18n.t('landing.how.step1_title')),
        el('p', { class: 'how-step-body' }, i18n.t('landing.how.step1_body')),
      ]),
      el('li', {}, [
        el('h3', { class: 'how-step-title' }, i18n.t('landing.how.step2_title')),
        el('p', { class: 'how-step-body' }, i18n.t('landing.how.step2_body')),
      ]),
      el('li', {}, [
        el('h3', { class: 'how-step-title' }, i18n.t('landing.how.step3_title')),
        el('p', { class: 'how-step-body' }, i18n.t('landing.how.step3_body')),
      ]),
      el('li', {}, [
        el('h3', { class: 'how-step-title' }, i18n.t('landing.how.step4_title')),
        el('p', { class: 'how-step-body' }, i18n.t('landing.how.step4_body')),
      ]),
    ]),
    el('p', { class: 'how-note' }, i18n.t('landing.how.note')),
  ]);

  const consentGroup = el('div', { class: 'consent-group' }, [
    el('label', { class: 'consent', for: 'age' }, [
      ageOk,
      el('span', {}, i18n.t('landing.age_gate')),
    ]),
    el('label', { class: 'consent', for: 'consent' }, [
      consent,
      el('span', {}, [
        i18n.t('landing.consent_label') + ' ',
        el(
          'a',
          {
            href: '#privacy',
            onClick: (ev: Event) => {
              ev.preventDefault();
              onOpenPrivacy();
            },
          },
          `(${i18n.t('landing.privacy_link')})`,
        ),
      ]),
    ]),
    el('label', { class: 'consent', for: 'expert-mode' }, [
      expertMode,
      el('span', {}, [
        i18n.t('expert.toggle'),
        el('span', { class: 'consent-help' }, ` — ${i18n.t('expert.help')}`),
      ]),
    ]),
  ]);

  // SEO.3 — discrete footer link for researchers. Not visually competing
  // with Begin; does not disclose axes/bias. Participants who follow it get
  // the full research brief at /about-research.
  const researchersLink = onOpenAboutResearch
    ? el(
        'p',
        { class: 'landing-researchers-link' },
        [
          el('a', {
            href: '/about-research',
            onClick: (e: Event) => { e.preventDefault(); onOpenAboutResearch(); },
          }, i18n.t('landing.researchers_link')),
        ],
      )
    : null;

  return el('div', {}, [
    topbar,
    el('section', { 'aria-label': 'Landing', class: 'stack-lg' }, [
      hero,
      sensitiveBanner,
      howItWorks,
      mission,
      sensitive,
      consentGroup,
      begin,
      researchersLink,
    ]),
  ]);
}
